import React, { useState, useEffect } from "react";
import "./RightSideBar.scss";
import button from "../../../../assets/images/homepage/Button.svg";
import buttonDark from "../../../../assets/images/homepage/Button Dark.svg";
import useTheme from "hooks/observer";
import useScreenMobile from "hooks/useScreen";

/* =========================
   Time-ago helper (UI only)
========================= */
const getTimeAgoLabel = (createdAt) => {
  if (!createdAt) return "just now";

  const diffMs = Date.now() - createdAt;
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffMin < 1440) return `${Math.floor(diffMin / 60)} hr ago`;
  return `${Math.floor(diffMin / 1440)} day ago`;
};

const LogHistorySidebar = ({
  history = [],
  handleDelete,
  handleClick,
  activeQuery,
  onToggle, // notify parent when open/close changes
  profile,
  isChartsView,
  isOpen: externalIsOpen,
}) => {
  const theme = useTheme();
  const isMobile = useScreenMobile({ size: 992 });

  // If mobile, default to closed. If desktop, use external or default to open.
  const [isOpen, setIsOpen] = useState(
    externalIsOpen !== undefined ? externalIsOpen : !isMobile
  );

  const safeHistory = Array.isArray(history) ? history : [];

  // Sync with external control
  useEffect(() => {
    if (externalIsOpen !== undefined) {
      setIsOpen(externalIsOpen);
    }
  }, [externalIsOpen]);

  // Sync back to parent
  useEffect(() => {
    onToggle?.(isOpen);
  }, [isOpen, onToggle]);

  // Add/remove body class to hide pagination when panel is open on mobile
  useEffect(() => {
    if (!isMobile) return;
    if (isOpen) {
      document.body.classList.add("sidebar-open");
    } else {
      document.body.classList.remove("sidebar-open");
    }
    return () => document.body.classList.remove("sidebar-open");
  }, [isOpen, isMobile]);

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      {/* =======================
          HISTORY SIDEBAR
          On mobile: toggle is absolutely positioned on the panel's left edge.
          overflow:visible on aside lets the toggle bleed outside.
      ======================= */}
      <aside
        className={
          `log-history ${isOpen ? "open" : "closed"} ` +
          (theme === "dark" ? "theme-dark" : "theme-light")
        }
        style={isMobile ? { overflow: "visible" } : undefined}
      >
        {/* ── Mobile toggle pinned on the panel's LEFT border ── */}
        {isMobile && (
          <div
            onClick={toggleSidebar}
            style={{
              position: "absolute",
              top: "60px",
              left: "-12px",          // straddles the panel's left edge
              width: "12px",
              height: "36px",
              backgroundColor: "#67308f",
              borderRadius: "4px 0 0 4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              zIndex: 221,
              boxShadow: "-2px 0 6px rgba(0,0,0,0.15)",
            }}
          >
            <svg
              width="6"
              height="10"
              viewBox="0 0 6 10"
              fill="none"
              style={{
                transform: isOpen ? "rotate(0deg)" : "rotate(180deg)",
                transition: "transform 0.3s",
              }}
            >
              <path
                d="M4.5 1L1 5l3.5 4"
                stroke="#fff"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        )}

        {/* Scrollable inner content (overflow on child, not aside) */}
        <div
          style={
            isMobile
              ? { overflowY: "auto", height: "100%" }
              : undefined
          }
        >
          <div className="history-header">
            <div className="history-title-row">
              <div className="history-title-left">
                <span className="history-icon">🕒</span>
                <h3>History</h3>
              </div>

              {safeHistory.length > 0 && (
                <button
                  className="history-clear-btn"
                  onClick={() => {
                    for (let i = safeHistory.length - 1; i >= 0; i--) {
                      handleDelete(i);
                    }
                  }}
                >
                  Clear all
                </button>
              )}
            </div>
          </div>

          <div className="history-content">
            <ul className="history-list">
              {safeHistory.length === 0 ? (
                <li className="history-empty">
                  <div className="history-empty-icon">🕒</div>
                  <div className="history-empty-title">No history yet</div>
                  <div className="history-empty-sub">
                    Run a query to see it here.
                  </div>
                </li>
              ) : (
                safeHistory.map((entry, idx) => (
                  <li
                    key={idx}
                    className={
                      "history-item" +
                      (entry.query === activeQuery ? " history-item--active" : "")
                    }
                  >
                    <button
                      className="history-item-main"
                      onClick={() => {
                        handleClick(entry);
                        if (isMobile) setIsOpen(false); // Auto-close on mobile
                      }}
                    >
                      <div className="history-item-title">
                        {entry.query || "list all logs"}
                      </div>

                      <div className="history-item-meta">
                        <span className="history-item-time">
                          {getTimeAgoLabel(entry.createdAt || Date.now())}
                        </span>
                      </div>
                    </button>

                    <button
                      className="history-delete-btn"
                      onClick={() => handleDelete(idx)}
                      title="Delete"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      </aside>

      {/* Desktop-only toggle button */}
      {!isMobile && (
        <div
          className={
            `toggle-btn ${isOpen ? "open" : "closed"} ` +
            (theme === "dark" ? "theme-dark" : "theme-light")
          }
          onClick={toggleSidebar}
        >
          <img
            src={theme === "light" ? button : buttonDark}
            alt="toggle"
          />
        </div>
      )}
    </>
  );
};

export default LogHistorySidebar;
